import { useCallback, useEffect, useRef, useState } from 'react';
import useSWR from 'swr';
import { adminApi } from '../../lib/api';
import type { AdminLogEvent } from '../../types';

const MINUTES_OPTIONS = [15, 60, 240, 1440];
const LIMIT_OPTIONS = [100, 500, 1000];
const AUTO_REFRESH_MS = 5000;

export function LogsPage() {
  const { data: envsData } = useSWR('admin/logs/environments', () => adminApi.listLogEnvironments());
  const { data: workloadsData } = useSWR('admin/logs/workloads', () => adminApi.listLogWorkloads());

  const environments = envsData?.environments ?? [];
  const workloads = workloadsData?.workloads ?? [];

  const [env, setEnv] = useState<string>('');
  const [workload, setWorkload] = useState<string>('');
  const [pattern, setPattern] = useState('');
  const [minutes, setMinutes] = useState(15);
  const [limit, setLimit] = useState(500);
  const [autoRefresh, setAutoRefresh] = useState(false);

  useEffect(() => {
    if (!env && envsData) {
      setEnv(envsData.current && environments.some((e) => e.key === envsData.current)
        ? envsData.current
        : (environments[0]?.key ?? ''));
    }
  }, [env, envsData, environments]);
  useEffect(() => {
    if (!workload && workloads.length > 0) {
      setWorkload(workloads[0].key);
    }
  }, [workload, workloads]);

  const ready = !!env && !!workload;

  const [result, setResult] = useState<{
    events: AdminLogEvent[];
    scannedBytes: number;
    recordsMatched: number;
    queryDurationMs: number;
    consoleUrl: string;
  } | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const requestIdRef = useRef(0);

  const runQuery = useCallback(async () => {
    if (!ready) return;
    const id = ++requestIdRef.current;
    setLoading(true);
    setError(null);
    try {
      const r = await adminApi.tailLogs({ env, workload, pattern, minutes, limit });
      if (id !== requestIdRef.current) return;
      setResult({
        events: r.events,
        scannedBytes: r.scannedBytes,
        recordsMatched: r.recordsMatched,
        queryDurationMs: r.queryDurationMs,
        consoleUrl: r.consoleUrl,
      });
    } catch (err) {
      if (id !== requestIdRef.current) return;
      setError(err instanceof Error ? err.message : 'Query failed');
    } finally {
      if (id === requestIdRef.current) setLoading(false);
    }
  }, [env, workload, pattern, minutes, limit, ready]);

  useEffect(() => { runQuery(); }, [runQuery]);

  useEffect(() => {
    if (!autoRefresh || !ready) return;
    let cancelled = false;
    const tick = () => {
      if (cancelled) return;
      if (document.visibilityState === 'visible') runQuery();
    };
    const handle = window.setInterval(tick, AUTO_REFRESH_MS);
    return () => { cancelled = true; window.clearInterval(handle); };
  }, [autoRefresh, ready, runQuery]);

  const isStaging = env === 'staging';
  const isProd = env === 'prod';

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-4">
        <h1 className="text-2xl font-semibold text-gray-100">Logs</h1>
        <EnvBadge env={env} isProd={isProd} isStaging={isStaging} />
      </div>

      <div className="bg-gray-900 border border-gray-800 rounded p-4 flex flex-wrap items-end gap-3">
        <Field label="Environment">
          <select
            value={env}
            onChange={(e) => setEnv(e.target.value)}
            className="bg-gray-950 border border-gray-800 rounded px-2 py-1.5 text-sm text-gray-100 min-w-[10rem]"
          >
            {environments.map((e) => (
              <option key={e.key} value={e.key}>
                {e.label}{e.isCurrent ? ' (this dashboard)' : ''}
              </option>
            ))}
          </select>
        </Field>

        <Field label="Workload">
          <select
            value={workload}
            onChange={(e) => setWorkload(e.target.value)}
            className="bg-gray-950 border border-gray-800 rounded px-2 py-1.5 text-sm text-gray-100 min-w-[14rem]"
          >
            {workloads.map((w) => (
              <option key={w.key} value={w.key}>{w.label}</option>
            ))}
          </select>
        </Field>

        <Field label="Filter (substring)">
          <input
            type="text"
            value={pattern}
            onChange={(e) => setPattern(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') runQuery(); }}
            placeholder="docId, error, …"
            className="bg-gray-950 border border-gray-800 rounded px-2 py-1.5 text-sm text-gray-100 w-64"
          />
        </Field>

        <Field label="Minutes">
          <select
            value={minutes}
            onChange={(e) => setMinutes(parseInt(e.target.value, 10))}
            className="bg-gray-950 border border-gray-800 rounded px-2 py-1.5 text-sm text-gray-100"
          >
            {MINUTES_OPTIONS.map((m) => (
              <option key={m} value={m}>{m === 1440 ? '24h' : `${m}m`}</option>
            ))}
          </select>
        </Field>

        <Field label="Limit">
          <select
            value={limit}
            onChange={(e) => setLimit(parseInt(e.target.value, 10))}
            className="bg-gray-950 border border-gray-800 rounded px-2 py-1.5 text-sm text-gray-100"
          >
            {LIMIT_OPTIONS.map((l) => (
              <option key={l} value={l}>{l}</option>
            ))}
          </select>
        </Field>

        <div className="flex items-center gap-2 ml-auto">
          <label className="flex items-center gap-1.5 text-sm text-gray-400 select-none cursor-pointer">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              className="accent-blue-500"
            />
            Auto-refresh 5s
          </label>
          <button
            onClick={runQuery}
            disabled={loading || !ready}
            className="px-3 py-1.5 text-sm rounded bg-gray-800 text-gray-100 border border-gray-700 hover:bg-gray-700 disabled:opacity-40"
          >
            {loading ? 'Loading…' : 'Refresh'}
          </button>
          {result?.consoleUrl && (
            <a
              href={result.consoleUrl}
              target="_blank"
              rel="noreferrer noopener"
              className="px-3 py-1.5 text-sm rounded bg-blue-900/50 text-blue-200 border border-blue-800 hover:bg-blue-900"
            >
              Open in CloudWatch ↗
            </a>
          )}
        </div>
      </div>

      {error && (
        <div className="bg-red-950/40 border border-red-900 text-red-200 rounded p-3 text-sm">
          {error}
        </div>
      )}

      <div className="bg-gray-900 border border-gray-800 rounded overflow-hidden">
        {!result && loading && (
          <div className="px-4 py-8 text-center text-gray-500 text-sm">Loading…</div>
        )}
        {result && result.events.length === 0 && (
          <div className="px-4 py-8 text-center text-gray-500 text-sm">No log events in this window.</div>
        )}
        {result && result.events.length > 0 && (
          <table className="w-full font-mono text-xs">
            <thead>
              <tr className="text-gray-500 text-left border-b border-gray-800 bg-gray-950">
                <th className="px-3 py-2 font-normal w-28">Time</th>
                <th className="px-3 py-2 font-normal w-16">Level</th>
                <th className="px-3 py-2 font-normal w-64">Pod</th>
                <th className="px-3 py-2 font-normal">Message</th>
              </tr>
            </thead>
            <tbody>
              {result.events.map((ev, i) => (
                <EventRow key={`${ev.timestamp}-${i}`} ev={ev} />
              ))}
            </tbody>
          </table>
        )}
      </div>

      {result && (
        <div className="text-xs text-gray-500 flex flex-wrap items-center gap-3">
          <span>{result.events.length} events</span>
          <span>·</span>
          <span>scanned {formatBytes(result.scannedBytes)}</span>
          <span>·</span>
          <span>matched {result.recordsMatched.toLocaleString()}</span>
          <span>·</span>
          <span>{result.queryDurationMs}ms</span>
          <span>·</span>
          <span>env={env}</span>
        </div>
      )}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1 text-xs text-gray-500">
      <span>{label}</span>
      {children}
    </label>
  );
}

function EnvBadge({ env, isProd, isStaging }: { env: string; isProd: boolean; isStaging: boolean }) {
  if (!env) return null;
  const cls = isProd
    ? 'bg-red-950/60 text-red-200 border-red-800 ring-1 ring-red-700/40'
    : isStaging
    ? 'bg-yellow-950/60 text-yellow-200 border-yellow-800 ring-1 ring-yellow-700/40'
    : 'bg-gray-800 text-gray-300 border-gray-700';
  return (
    <span className={`text-xs font-medium px-2.5 py-1 rounded border ${cls}`}>
      Viewing: {env.toUpperCase()}
    </span>
  );
}

function EventRow({ ev }: { ev: AdminLogEvent }) {
  const [open, setOpen] = useState(false);
  const ts = ev.timestamp ? formatTimestamp(ev.timestamp) : '—';
  const hasMeta = !!(ev.meta && Object.keys(ev.meta).length > 0);
  const hasRaw = !!ev.raw && ev.raw !== JSON.stringify(ev.meta);
  const expandable = hasMeta || hasRaw;
  const messageColor =
    ev.level === 'error' ? 'text-red-300'
    : ev.level === 'warn' ? 'text-yellow-200'
    : ev.level === 'debug' ? 'text-gray-400'
    : 'text-gray-200';

  return (
    <>
      <tr
        className={`border-b border-gray-800 hover:bg-gray-800/30 align-top ${expandable ? 'cursor-pointer' : ''}`}
        onClick={() => expandable && setOpen((o) => !o)}
      >
        <td className="px-3 py-1.5 text-gray-500 whitespace-nowrap">{ts}</td>
        <td className="px-3 py-1.5"><LevelBadge level={ev.level} /></td>
        <td className="px-3 py-1.5 text-blue-400 truncate max-w-0" title={ev.podName}>
          {shortenPodName(ev.podName)}
        </td>
        <td className={`px-3 py-1.5 break-words ${messageColor}`}>
          <div className="flex items-start gap-2">
            <span className="flex-1">{ev.message || <span className="text-gray-600 italic">(empty)</span>}</span>
            {hasMeta && <MetaPreview meta={ev.meta!} />}
            {expandable && (
              <span className="text-gray-600 text-xs select-none shrink-0">{open ? '▾' : '▸'}</span>
            )}
          </div>
        </td>
      </tr>
      {open && expandable && (
        <tr className="bg-gray-950/60 border-b border-gray-800">
          <td colSpan={4} className="px-3 py-3">
            {hasMeta && (
              <div className="mb-2">
                <div className="text-[10px] uppercase tracking-wide text-gray-500 mb-1">Meta</div>
                <pre className="text-xs text-gray-200 whitespace-pre-wrap break-words bg-black/40 border border-gray-800 rounded p-2 max-h-96 overflow-auto">
                  {JSON.stringify(ev.meta, null, 2)}
                </pre>
              </div>
            )}
            {hasRaw && (
              <div>
                <div className="text-[10px] uppercase tracking-wide text-gray-500 mb-1">Raw</div>
                <pre className="text-[11px] text-gray-400 whitespace-pre-wrap break-all bg-black/40 border border-gray-800 rounded p-2 max-h-72 overflow-auto">
                  {ev.raw}
                </pre>
              </div>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

function MetaPreview({ meta }: { meta: Record<string, unknown> }) {
  // Tiny inline pill showing 1–2 high-signal meta keys when present
  // (documentId/jobId/batchName) — saves a click for the most common debugging case.
  const priorityKeys = ['documentId', 'jobId', 'batchName', 'batchId', 'flowId'];
  const shown = priorityKeys.filter((k) => meta[k] != null).slice(0, 2);
  if (shown.length === 0) {
    const count = Object.keys(meta).length;
    return <span className="text-[10px] text-gray-500 shrink-0">+{count} fields</span>;
  }
  return (
    <span className="flex items-center gap-1 shrink-0">
      {shown.map((k) => (
        <span key={k} className="text-[10px] bg-gray-800 text-gray-300 rounded px-1.5 py-0.5">
          {k}={truncateValue(meta[k])}
        </span>
      ))}
    </span>
  );
}

function LevelBadge({ level }: { level: string | null }) {
  if (!level) return <span className="text-gray-600">—</span>;
  const cls =
    level === 'error' ? 'bg-red-900/50 text-red-200 border-red-800'
    : level === 'warn' ? 'bg-yellow-900/40 text-yellow-200 border-yellow-800'
    : level === 'info' ? 'bg-gray-800 text-gray-300 border-gray-700'
    : 'bg-gray-900 text-gray-500 border-gray-800';
  return (
    <span className={`text-[10px] uppercase font-medium px-1.5 py-0.5 rounded border ${cls}`}>
      {level}
    </span>
  );
}

function truncateValue(v: unknown): string {
  const s = typeof v === 'string' ? v : JSON.stringify(v);
  return s.length > 24 ? `${s.slice(0, 24)}…` : s;
}

function shortenPodName(pod: string): string {
  // Pod names look like `iconect-jobs-staging-worker-fetch-65d8b6f9b6-xyz12`. The
  // last two segments are the ReplicaSet+pod hash — keep them but drop the long
  // workload prefix to save column space; full name is in the title attribute.
  const parts = pod.split('-');
  if (parts.length < 4) return pod;
  const hash = parts.slice(-2).join('-');
  const role = parts.slice(0, -2).slice(-2).join('-'); // e.g. "worker-fetch"
  return `…${role}-${hash}`;
}

function formatTimestamp(iso: string): string {
  // CloudWatch returns "2026-05-12 18:42:13.123" (space-separated UTC). Render local hh:mm:ss.
  const t = iso.replace(' ', 'T') + 'Z';
  const d = new Date(t);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleTimeString();
}

function formatBytes(n: number): string {
  if (!n) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  let i = 0;
  let v = n;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i++; }
  return `${v < 10 ? v.toFixed(1) : Math.round(v)} ${units[i]}`;
}
